import { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { X, Loader2, Wand2, Check, MapPin, SplitSquareHorizontal } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { useCerrarConAtras } from "../../utils/useCerrarConAtras";
import { opts } from "./constants";

const PAGE_SIZE = 50;

// CEN 2.0 FASE 2 sub-entrega 2 — Asignación asistida de nivel_id por ítem, con
// detección de rangos ("del piso 7 al 40") en el título de la sección ancestro
// y distribución equitativa editable. `selected` nunca se pre-asigna sola sin
// acción del usuario (mismo criterio que AsignarTareasModal).
export function AsignarNivelesModal({ open, budgetId, onClose, onPatchItems, onReplaceItems }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [distribuyendo, setDistribuyendo] = useState(null); // item_uid en curso
  // Fix AK (2026-09-04): "todos estos items corresponden a una planta tipo que
  // cuenta con 7 plantas (…) debería poder asignar mas niveles a esos items"
  // (Edson, 04/09). Distribución manual: se eligen los niveles a mano, sin
  // depender de que el título de la sección tenga un rango escrito.
  const [distPanel, setDistPanel] = useState(null);   // { uids: [], nivelesSel: [] }
  const [distProgreso, setDistProgreso] = useState(null);
  const [marcadas, setMarcadas] = useState(new Set()); // filas marcadas para distribuir
  const [rows, setRows] = useState([]);
  const [niveles, setNiveles] = useState([]);
  const [filterStatus, setFilterStatus] = useState("sin_nivel");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  // Fix BK (2026-09-08): grupos desplegados (movido acá arriba, junto a los demás
  // hooks, para no violar rules-of-hooks: hay un `if (!open) return null;` más
  // abajo y un hook no puede quedar después de un return condicional).
  const [gruposAbiertos, setGruposAbiertos] = useState(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/budgets/${budgetId}/match-niveles`, opts);
      setNiveles(data.niveles || []);
      setRows((data.items || []).map(r => ({ ...r, selected: null })));
      setPage(0);
      // Fix CB (2026-09-10): "una vez que se actualizó el estado ya tiene que quedar
      // deseleccionado" (Edson, 10/09). Esta recarga corre al abrir y después de
      // guardar, asignar, distribuir o deshacer: lo marcado ya se aplicó (o dejó de
      // existir) y no puede sobrevivir. Antes, una fila marcada que recibía nivel
      // con "Confirmar y guardar" quedaba tildada con la casilla bloqueada y
      // "1 ítem marcado" arriba, sin forma de sacarla.
      setMarcadas(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar sugerencias de niveles");
    } finally { setLoading(false); }
  }, [budgetId]);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  // Fix AI (2026-09-04): el botón "atrás" del navegador cierra este modal.
  useCerrarConAtras(open, onClose);

  const filtered = useMemo(() => rows.filter(r => {
    const matchStatusF = filterStatus === "todos" ? true
      : filterStatus === "sin_nivel" ? !r.nivel_id_actual
      : filterStatus === "rango" ? r.propuesta?.tipo === "rango"
      : true;
    // Fix BK (2026-09-08): "seria bueno tambien poder filtrar por ese texto,
    // porque ahi comunmente figura el nivel" (Edson, 08/09). El nivel casi siempre
    // está escrito en la sección ancestro (SUBSUELO, PLANTA TIPO), así que buscar
    // ahí es lo que ahorra tiempo al asignar.
    const q = (search || "").toLowerCase();
    const matchSearch = !search
      || (r.descripcion || "").toLowerCase().includes(q)
      || (r.seccion_ancestro || "").toLowerCase().includes(q)
      || (r.numero || "").toLowerCase().includes(q);
    return matchStatusF && matchSearch;
  }), [rows, filterStatus, search]);

  // Fix BK (2026-09-08): "cuando se asignan varios niveles que no se multipliquen
  // las filas, que haya una columna que muestre el nivel o los niveles aplicados a
  // esa fila del item nomas" (Edson, 08/09). Las copias que salieron de una misma
  // distribución se juntan en un solo renglón; se despliega si hay que corregir el
  // nivel de alguna en particular.
  const claveGrupo = (r) => `${(r.numero || "").trim()}|${(r.descripcion || "").trim().toLowerCase()}`;

  const filas = useMemo(() => {
    const out = [];
    const idx = {};
    filtered.forEach(r => {
      if (!r.origen_item_uid) { out.push({ tipo: "fila", clave: r.item_uid, r }); return; }
      const k = claveGrupo(r);
      if (!idx[k]) { idx[k] = { tipo: "grupo", clave: k, base: r, hijos: [] }; out.push(idx[k]); }
      idx[k].hijos.push(r);
    });
    // Un grupo de una sola línea no es un grupo.
    return out.map(x => (x.tipo === "grupo" && x.hijos.length < 2)
      ? { tipo: "fila", clave: x.hijos[0].item_uid, r: x.hijos[0] }
      : x);
  }, [filtered]);

  // Fix CB (2026-09-10): a qué reparto pertenece cada copia se cuenta sobre TODAS
  // las filas, no sobre las filtradas. `filas` agrupa lo que el filtro deja ver:
  // con "Sin nivel asignado", la única copia sin nivel de un reparto de 7 se
  // dibuja sola. Y una copia que quedó sola en el presupuesto (INVICTA HERRERA,
  // 3.29.9 a 3.29.16) se trataba igual que un reparto de 7: casilla bloqueada,
  // "ya distribuido" y ningún camino para repartirla. Solo es "parte de un
  // reparto" la copia que comparte número y descripción con al menos otra — la
  // misma familia del renglón "×N líneas" (Fix BK) y la que usa el backend para
  // frenar un reparto repetido (`_lineas_del_reparto`, Fix CB).
  const familias = useMemo(() => {
    const m = {};
    rows.forEach(r => {
      if (!r.origen_item_uid) return;
      const k = claveGrupo(r);
      (m[k] = m[k] || []).push(r);
    });
    return m;
  }, [rows]);
  const familiaDe = (r) => (r.origen_item_uid ? (familias[claveGrupo(r)] || []) : []);
  const enReparto = (r) => familiaDe(r).length >= 2;

  const pageFilas = filas.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const pageRows = pageFilas.flatMap(f => (f.tipo === "grupo" ? f.hijos : [f.r]));
  const totalPages = Math.max(1, Math.ceil(filas.length / PAGE_SIZE));

  if (!open) return null;

  if (!loading && niveles.length === 0) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="asignar-niveles-modal">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center space-y-3">
          <p className="text-sm text-gray-600">Este presupuesto no tiene niveles definidos todavía. Agregalos en la pestaña "Alcance / Observaciones" y guardá antes de usar esta herramienta.</p>
          <button onClick={onClose} className="w-full border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cerrar</button>
        </div>
      </div>
    );
  }

  const nSinNivel = rows.filter(r => !r.nivel_id_actual).length;

  const setRowSelected = (uid, value) => setRows(prev => prev.map(r => r.item_uid === uid ? { ...r, selected: value } : r));

  // Fix CB (2026-09-10): la sugerencia se aplicaba a TODAS las filas con propuesta
  // única — también a las que ya tenían nivel y a las copias de un reparto, aunque
  // el filtro por defecto ("Sin nivel asignado") las oculte. Un clic cambiaba
  // Nivel 1…7 por "Planta Baja" en ítems de PLANTA TIPO sin que nadie lo viera en
  // pantalla. Ahora solo completa las filas que no tienen nivel.
  const aplicaSugerencia = (r) => r.selected === null && r.propuesta?.tipo === "unico"
    && !r.nivel_id_actual && !enReparto(r);
  const acceptSuggested = () => {
    const count = rows.filter(aplicaSugerencia).length;
    setRows(prev => prev.map(r => {
      if (aplicaSugerencia(r)) return { ...r, selected: r.propuesta.nivel_id };
      return r;
    }));
    toast.success(count > 0 ? `${count} sugerencia(s) aplicada(s) — revisá y guardá` : "No hay sugerencias únicas pendientes (los rangos se distribuyen aparte)");
  };

  const handleSave = async () => {
    const mappings = rows.filter(r => r.selected !== null)
      .map(r => ({ item_uid: r.item_uid, nivel_id: r.selected === "__clear__" ? null : r.selected }));
    if (mappings.length === 0) { toast.info("No hay cambios para guardar"); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/budgets/${budgetId}/items-niveles`, { mappings }, opts);
      const byUid = {};
      mappings.forEach(m => { byUid[m.item_uid] = m.nivel_id; });
      onPatchItems(byUid);
      toast.success(`${data.updated} ítem${data.updated !== 1 ? "s" : ""} actualizado${data.updated !== 1 ? "s" : ""}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar los niveles");
    } finally { setSaving(false); }
  };

  // Fix BX (2026-09-09): "ya quite la Planta Baja que estaba en esos items pero
  // no me habilita distribuir en varios niveles" (Edson, 09/09). Quitar el nivel
  // no vuelve original a un clon: sigue teniendo origen_item_uid y el guardia del
  // Fix BE lo bloquea. Este es el camino de vuelta.
  const handleDeshacer = async (fila) => {
    const n = fila.hijos.length;
    if (!window.confirm(
      `Se van a unificar las ${n} líneas de "${fila.base.descripcion}" en una sola, ` +
      `sumando sus cantidades. Después vas a poder distribuirla de nuevo. ¿Seguimos?`
    )) return;
    setDistribuyendo(fila.hijos[0].item_uid);
    try {
      const { data } = await axios.post(
        `${API}/budgets/${budgetId}/items/${fila.hijos[0].item_uid}/deshacer-distribucion`, {}, opts);
      onReplaceItems(data.items || []);
      const info = data.deshacer || {};
      toast.success(`${info.lineas_unificadas || n} líneas unificadas en una, cantidad ${info.cantidad_total}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo deshacer la distribución");
    } finally { setDistribuyendo(null); }
  };

  const handleDistribuir = async (row) => {
    const nivelIds = row.propuesta.niveles;
    setDistribuyendo(row.item_uid);
    try {
      const { data } = await axios.post(`${API}/budgets/${budgetId}/items/${row.item_uid}/distribuir-niveles`, { nivel_ids: nivelIds }, opts);
      onReplaceItems(data.items || []);
      toast.success(`Ítem distribuido en ${nivelIds.length} niveles (cantidad ${row.propuesta.cantidad_por_nivel} c/u)`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al distribuir el ítem");
    } finally { setDistribuyendo(null); }
  };

  // Fix AK (2026-09-04): distribución con los niveles elegidos a mano, para uno o
  // varios ítems. Una llamada por ítem, en orden y esperando cada una: el
  // endpoint reescribe el presupuesto entero en cada llamada.
  const abrirDistribuir = (uids) => {
    const sugeridos = rows.find(r => uids.includes(r.item_uid))?.propuesta;
    setDistPanel({ uids, nivelesSel: sugeridos?.tipo === "rango" ? [...sugeridos.niveles] : [] });
  };

  const toggleNivelSel = (id) => setDistPanel(p => ({
    ...p,
    nivelesSel: p.nivelesSel.includes(id) ? p.nivelesSel.filter(x => x !== id) : [...p.nivelesSel, id],
  }));

  const confirmarDistribuir = async () => {
    const { uids, nivelesSel } = distPanel;
    if (nivelesSel.length < 1) { toast.error("Elegí al menos un nivel"); return; }
    // Fix CB (2026-09-10): "que con la opción de seleccionar varios se pueda asignar
    // un solo nivel, porque ahora solo permite más de uno" (Edson, 10/09). Con UN
    // nivel no se parte nada: se les asigna ese nivel a todos los ítems marcados,
    // con el mismo endpoint que usa "Confirmar y guardar". Con dos o más sigue
    // siendo el reparto de siempre, sin cambios.
    if (nivelesSel.length === 1) {
      const nivelId = nivelesSel[0];
      setDistProgreso({ hechos: 0, total: uids.length });
      try {
        const mappings = uids.map(uid => ({ item_uid: uid, nivel_id: nivelId }));
        const { data } = await axios.put(`${API}/budgets/${budgetId}/items-niveles`, { mappings }, opts);
        const byUid = {};
        mappings.forEach(m => { byUid[m.item_uid] = m.nivel_id; });
        onPatchItems(byUid);
        toast.success(`${data.updated} ítem${data.updated !== 1 ? "s" : ""} en ${etiquetaNivel(nivelId)}`);
        setDistPanel(null);
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error al asignar el nivel");
      } finally { setDistProgreso(null); }
      return;
    }
    let ultimo = null, hechos = 0;
    setDistProgreso({ hechos: 0, total: uids.length });
    try {
      for (const uid of uids) {
        const { data } = await axios.post(`${API}/budgets/${budgetId}/items/${uid}/distribuir-niveles`, { nivel_ids: nivelesSel }, opts);
        ultimo = data; hechos += 1;
        setDistProgreso({ hechos, total: uids.length });
      }
      if (ultimo) onReplaceItems(ultimo.items || []);
      toast.success(`${hechos} ítem${hechos !== 1 ? "s" : ""} distribuido${hechos !== 1 ? "s" : ""} en ${nivelesSel.length} niveles`);
      setDistPanel(null);
      setMarcadas(new Set());
      fetchData();
    } catch (err) {
      if (ultimo) onReplaceItems(ultimo.items || []);
      toast.error(`${err.response?.data?.detail || "Error al distribuir"}${hechos ? ` · ${hechos} ítem(s) sí se distribuyeron` : ""}`);
      fetchData();
    } finally { setDistProgreso(null); }
  };

  const toggleGrupo = (k) => setGruposAbiertos(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const etiquetaNivel = (id) => (niveles.find(n => n.id === id) || {}).label || id;

  // Fix BK (2026-09-08): el renglón de un ítem. `esHijo` es una de las líneas de
  // un grupo desplegado, y se dibuja apenas corrida para que se lea como tal.
  const filaDeItem = (r, esHijo) => (
    <tr key={r.item_uid} className={`border-t border-gray-100 ${marcadas.has(r.item_uid) ? "bg-violet-50/40" : ""} ${esHijo ? "bg-gray-50/60" : ""}`} data-testid={`asignar-niveles-row-${r.item_uid}`}>
      <td className="px-2 py-2 text-center">
        {/* Fix CB (2026-09-10): "todos los items debe permitir modificar" (Edson,
            10/09). Se marca cualquier ítem —con nivel, sin nivel o copia que quedó
            sola—, salvo las copias de un reparto de varias líneas (esas se corrigen
            con "Deshacer distribución"). Y una fila marcada siempre se puede
            desmarcar: la casilla ya no se bloquea con el tilde puesto. */}
        <input type="checkbox" checked={marcadas.has(r.item_uid)} onChange={() => toggleMarcada(r.item_uid)}
          disabled={!marcadas.has(r.item_uid) && enReparto(r)}
          title={enReparto(r) ? "Este ítem es parte de un reparto en varios niveles" : ""}
          className="w-3.5 h-3.5 accent-violet-600 disabled:opacity-30" data-testid={`asignar-niveles-marcar-${r.item_uid}`} />
      </td>
      <td className={`px-3 py-2 font-semibold text-gray-800 max-w-[200px] whitespace-normal break-words ${esHijo ? "pl-6 font-normal text-gray-600" : ""}`} title={r.descripcion}>
        {esHijo ? `↳ ${r.numero}` : `${r.numero} — ${r.descripcion}`}
      </td>
      <td className="px-3 py-2 whitespace-nowrap" data-testid={`asignar-niveles-actual-${r.item_uid}`}>
        {r.nivel_id_actual ? (
          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#EBF3FB] text-[#1A4B7A]">
            {etiquetaNivel(r.nivel_id_actual)}
          </span>
        ) : <span className="text-[10px] text-gray-300">sin nivel</span>}
        {/* Fix CB (2026-09-10): "no muestra directamente a que nivel o niveles fueron
            asignados (…) los que ya esten se tiene que ver los niveles asignados"
            (Edson, 10/09). "ya distribuido" salía en copias que quedaron solas —sin
            nivel y sin nada que mostrar— y en la copia de un reparto que el filtro
            deja ver de a una. Ahora la copia sola es un ítem más, y la que es parte
            de un reparto muestra los niveles de TODAS sus líneas. */}
        {enReparto(r) && !esHijo && (
          <div className="mt-1" data-testid={`asignar-niveles-reparto-${r.item_uid}`}>
            <p className="text-[9px] text-amber-700 font-semibold">repartido en {familiaDe(r).length} líneas:</p>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {familiaDe(r).map(h => (
                <span key={h.item_uid} className="inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[#EBF3FB] text-[#1A4B7A]">
                  {h.nivel_id_actual ? etiquetaNivel(h.nivel_id_actual) : "sin nivel"}
                </span>
              ))}
            </div>
          </div>
        )}
      </td>
      <td className="px-3 py-2 max-w-[220px]">
        {!esHijo && (
          <div className="whitespace-normal break-words text-gray-500" title={r.seccion_ancestro}>{r.seccion_ancestro || "—"}</div>
        )}
        {!esHijo && (r.propuesta?.tipo === "rango" ? (
          <button onClick={() => handleDistribuir(r)} disabled={distribuyendo === r.item_uid}
            className="mt-1 flex items-center gap-1 text-[10px] font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5 hover:bg-violet-100 disabled:opacity-50"
            data-testid={`asignar-niveles-distribuir-${r.item_uid}`}>
            {distribuyendo === r.item_uid ? <Loader2 size={10} className="animate-spin" /> : <SplitSquareHorizontal size={10} />}
            Distribuir en {r.propuesta.niveles.length} niveles ({r.propuesta.cantidad_por_nivel} c/u)
          </button>
        ) : r.propuesta?.tipo === "unico" ? (
          <span className="text-[10px] font-bold text-emerald-700">Sugerido: {etiquetaNivel(r.propuesta.nivel_id)} · {Math.round(r.propuesta.score * 100)}%</span>
        ) : <span className="text-[10px] text-gray-300">sin sugerencia</span>)}
        {/* Fix CB (2026-09-10): ninguna fila queda sin salida. La copia de un
            reparto ofrece deshacerlo acá mismo (antes solo desde el renglón
            "×N líneas", que el filtro "Sin nivel asignado" puede ocultar); cualquier
            otro ítem —con nivel, sin nivel o copia que quedó sola— se asigna o se
            reparte desde el mismo enlace. */}
        {!esHijo && (enReparto(r) ? (
          <button onClick={() => handleDeshacer({ base: r, hijos: familiaDe(r) })}
            disabled={distribuyendo === familiaDe(r)[0]?.item_uid}
            className="mt-1 text-[10px] font-semibold text-amber-700 hover:underline disabled:opacity-50"
            data-testid={`asignar-niveles-deshacer-fila-${r.item_uid}`}>
            {distribuyendo === familiaDe(r)[0]?.item_uid ? "Deshaciendo…" : "Deshacer distribución"}
          </button>
        ) : (
          <button onClick={() => abrirDistribuir([r.item_uid])}
            className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-violet-700 hover:underline"
            data-testid={`asignar-niveles-distribuir-manual-${r.item_uid}`}>
            <SplitSquareHorizontal size={10} /> {r.nivel_id_actual ? "Cambiar o repartir en niveles…" : "Distribuir en varios niveles…"}
          </button>
        ))}
      </td>
      <td className="px-3 py-2">
        <select value={r.selected ?? ""} onChange={e => setRowSelected(r.item_uid, e.target.value || null)}
          className="w-full border border-gray-200 rounded-lg px-2 py-1 text-[11px] bg-white"
          data-testid={`asignar-niveles-select-${r.item_uid}`}>
          <option value="">— Sin cambios —</option>
          <option value="__clear__">Sin asignar (borrar)</option>
          {niveles.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
        </select>
      </td>
    </tr>
  );

  const toggleMarcada = (uid) => setMarcadas(prev => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    return next;
  });

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="asignar-niveles-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <MapPin size={16} className="text-rose-500" /> Asignar Niveles por ítem
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">{loading ? "Cargando..." : `${nSinNivel} ítem${nSinNivel !== 1 ? "s" : ""} sin nivel asignado`}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="asignar-niveles-close-btn"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-3 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" size={28} /></div>
          ) : (
            <>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Se comparan los ítems ya guardados del presupuesto. Guardá los cambios pendientes ("Guardar Avance") antes de usar esta herramienta.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Buscar por descripción, sección o número…"
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs flex-1 min-w-[180px]" data-testid="asignar-niveles-search" />
                <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="asignar-niveles-filter">
                  <option value="sin_nivel">Sin nivel asignado</option>
                  <option value="todos">Todos</option>
                  <option value="rango">Con propuesta de rango</option>
                </select>
                <button onClick={acceptSuggested}
                  className="flex-shrink-0 flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold"
                  data-testid="asignar-niveles-accept-btn">
                  <Check size={13} /> Aceptar sugerencias únicas
                </button>
              </div>
              {/* Fix AK (2026-09-04): varios ítems de la misma planta tipo, de una sola vez. */}
              {marcadas.size > 0 && (
                <div className="flex items-center gap-3 px-4 py-2 bg-violet-50 border-2 border-violet-200 rounded-xl" data-testid="asignar-niveles-lote-bar">
                  <SplitSquareHorizontal size={14} className="text-violet-600 flex-shrink-0" />
                  <span className="text-xs font-bold text-violet-800">{marcadas.size} ítem{marcadas.size !== 1 ? "s" : ""} marcado{marcadas.size !== 1 ? "s" : ""}</span>
                  <button onClick={() => setMarcadas(new Set())} className="text-xs font-medium text-violet-700 hover:underline">Limpiar</button>
                  <button onClick={() => abrirDistribuir([...marcadas])}
                    className="ml-auto flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold"
                    data-testid="asignar-niveles-lote-btn">
                    {/* Fix CB (2026-09-10): con un nivel asigna, con varios reparte. */}
                    <SplitSquareHorizontal size={12} /> Asignar o distribuir niveles
                  </button>
                </div>
              )}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#EBF3FB]">
                    <tr>
                      {/* Fix AK (2026-09-04): marcado para distribuir en lote. */}
                      <th className="w-9 px-2 py-2">
                        {/* Fix BE (2026-09-08): "Todos" marca solo los que todavía no
                            tienen nivel. Marcar los ya repartidos era el camino directo
                            a distribuirlos por segunda vez. */}
                        <button onClick={() => setMarcadas(prev => {
                          // Fix CB (2026-09-10): una copia que quedó sola ya no es un
                          // reparto: si no tiene nivel entra en "Todos" como cualquier
                          // ítem sin nivel. Siguen afuera las que ya tienen nivel (se
                          // marcan de a una, a propósito) y las copias de un reparto.
                          const visibles = pageRows.filter(r => !r.nivel_id_actual && !enReparto(r)).map(r => r.item_uid);
                          const todas = visibles.every(id => prev.has(id));
                          const next = new Set(prev);
                          visibles.forEach(id => todas ? next.delete(id) : next.add(id));
                          return next;
                        })} className="text-[#1A4B7A] text-[10px] font-bold hover:underline" data-testid="asignar-niveles-marcar-todos">Todos</button>
                      </th>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Ítem</th>
                      {/* Fix BE (2026-09-08): sin esta columna, siete clones ya
                          repartidos en los niveles 1 al 7 se veían como siete filas
                          idénticas y se volvían a distribuir sin querer. */}
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Nivel actual</th>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Sección ancestro / propuesta</th>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Nivel a aplicar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Fix BK (2026-09-08): una fila por ítem. Las copias de una
                        distribución se resumen en un renglón con todos sus niveles
                        y se despliegan solo si hay que corregir alguna. */}
                    {pageFilas.map(f => f.tipo === "grupo" ? (
                      <Fragment key={f.clave}>
                        <tr className="border-t border-gray-100 bg-[#F7FAFD]" data-testid={`asignar-niveles-grupo-${f.clave}`}>
                          <td className="px-2 py-2 text-center">
                            <input type="checkbox" disabled title="Este ítem ya está repartido en varios niveles"
                              className="w-3.5 h-3.5 accent-violet-600 disabled:opacity-30" />
                          </td>
                          <td className="px-3 py-2 font-semibold text-gray-800 max-w-[200px] whitespace-normal break-words" title={f.base.descripcion}>
                            {f.base.numero} — {f.base.descripcion}
                            <span className="ml-1.5 inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-violet-100 text-violet-700 align-middle">
                              ×{f.hijos.length} líneas
                            </span>
                          </td>
                          <td className="px-3 py-2" data-testid={`asignar-niveles-grupo-niveles-${f.clave}`}>
                            <div className="flex flex-wrap gap-1">
                              {f.hijos.map(h => (
                                <span key={h.item_uid} className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#EBF3FB] text-[#1A4B7A]">
                                  {h.nivel_id_actual ? etiquetaNivel(h.nivel_id_actual) : "sin nivel"}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2 max-w-[220px]">
                            <div className="whitespace-normal break-words text-gray-500" title={f.base.seccion_ancestro}>{f.base.seccion_ancestro || "—"}</div>
                            <p className="mt-1 text-[10px] text-gray-400">Repartido en {f.hijos.length} niveles</p>
                          </td>
                          <td className="px-3 py-2">
                            <button onClick={() => toggleGrupo(f.clave)}
                              className="text-[11px] font-semibold text-violet-700 hover:underline"
                              data-testid={`asignar-niveles-ver-lineas-${f.clave}`}>
                              {gruposAbiertos.has(f.clave) ? "Ocultar las líneas" : `Ver las ${f.hijos.length} líneas`}
                            </button>
                            {/* Fix BX (2026-09-09): la salida cuando el reparto quedó mal.
                                Junta las líneas en una y la deja lista para repartir bien. */}
                            <button onClick={() => handleDeshacer(f)}
                              disabled={distribuyendo === f.hijos[0].item_uid}
                              className="block mt-1 text-[11px] font-semibold text-amber-700 hover:underline disabled:opacity-50"
                              data-testid={`asignar-niveles-deshacer-${f.clave}`}>
                              {distribuyendo === f.hijos[0].item_uid ? "Deshaciendo…" : "Deshacer distribución"}
                            </button>
                          </td>
                        </tr>
                        {gruposAbiertos.has(f.clave) && f.hijos.map(r => filaDeItem(r, true))}
                      </Fragment>
                    ) : filaDeItem(f.r, false))}
                    {pageFilas.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">Sin resultados para este filtro.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 text-xs text-gray-500">
                  <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-2 py-1 border rounded disabled:opacity-40">Anterior</button>
                  Página {page + 1} de {totalPages}
                  <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="px-2 py-1 border rounded disabled:opacity-40">Siguiente</button>
                </div>
              )}
            </>
          )}
        </div>
        {/* Fix AK (2026-09-04): panel de distribución manual. */}
        {distPanel && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="distribuir-niveles-panel">
            <div className="absolute inset-0 bg-black/40" onClick={() => !distProgreso && setDistPanel(null)} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-full overflow-y-auto p-5 space-y-3">
              <div>
                <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <SplitSquareHorizontal size={15} className="text-violet-600" /> Asignar o distribuir niveles
                </h4>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {distPanel.uids.length === 1
                    ? rows.find(r => r.item_uid === distPanel.uids[0])?.descripcion
                    : `${distPanel.uids.length} ítems marcados`}
                </p>
              </div>
              {/* Fix CB (2026-09-10): el mismo panel asigna (un nivel) o reparte (dos o más). */}
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Con <b>un</b> nivel, se les asigna ese nivel a los ítems, sin partirlos. Con <b>dos o más</b>, cada ítem se parte en una copia por nivel y su cantidad se divide en partes iguales. Los precios unitarios no cambian.
              </p>
              {/* Fix CB (2026-09-10): al aplicar, la tabla se recarga y los cambios que
                  todavía no se guardaron en "Nivel a aplicar" se pierden. Se avisa antes. */}
              {rows.some(r => r.selected !== null) && (
                <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="distribuir-aviso-pendientes">
                  Tenés {rows.filter(r => r.selected !== null).length} cambio(s) sin guardar en "Nivel a aplicar": al aplicar esto se descartan. Guardalos antes con "Confirmar y guardar".
                </p>
              )}
              <div className="border border-gray-200 rounded-xl max-h-56 overflow-y-auto divide-y divide-gray-100">
                {niveles.map(n => (
                  <label key={n.id} className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={distPanel.nivelesSel.includes(n.id)} onChange={() => toggleNivelSel(n.id)}
                      className="w-3.5 h-3.5 accent-violet-600" data-testid={`distribuir-nivel-${n.id}`} />
                    <span className="text-gray-700">{n.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-gray-600">
                {distPanel.nivelesSel.length === 0
                  ? "Elegí un nivel para asignarlo, o varios para repartir."
                  : distPanel.nivelesSel.length === 1
                    ? `${distPanel.uids.length} ítem${distPanel.uids.length !== 1 ? "s" : ""} → ${etiquetaNivel(distPanel.nivelesSel[0])}, sin partir.`
                  : distPanel.uids.length === 1
                    ? `Cantidad actual ${rows.find(r => r.item_uid === distPanel.uids[0])?.cantidad ?? "?"} → ${distPanel.nivelesSel.length} copias (la cantidad se divide entre ${distPanel.nivelesSel.length}).`
                    : `${distPanel.uids.length} ítems × ${distPanel.nivelesSel.length} niveles = ${distPanel.uids.length * distPanel.nivelesSel.length} ítems, cada uno con su cantidad dividida entre ${distPanel.nivelesSel.length}.`}
              </p>
              <div className="flex gap-2 justify-end pt-1">
                <button onClick={() => setDistPanel(null)} disabled={!!distProgreso}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 disabled:opacity-50">Cancelar</button>
                <button onClick={confirmarDistribuir} disabled={distPanel.nivelesSel.length < 1 || !!distProgreso}
                  className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-xs font-bold"
                  data-testid="distribuir-confirmar">
                  {distProgreso ? <Loader2 size={13} className="animate-spin" /> : <SplitSquareHorizontal size={13} />}
                  {distProgreso
                    ? (distPanel.nivelesSel.length === 1 ? "Asignando..." : `Distribuyendo ${distProgreso.hechos}/${distProgreso.total}...`)
                    : (distPanel.nivelesSel.length === 1 ? "Asignar nivel" : "Distribuir")}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="px-6 pb-6 pt-2 flex gap-3 flex-shrink-0">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving || loading}
            className="flex-1 flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
            data-testid="asignar-niveles-save-btn">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}
            {saving ? "Guardando..." : "Confirmar y guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
